import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  ArrowLeft,
  User,
  Truck,
  PackageCheck,
  Receipt,
  Download,
  Check,
  X,
  Wallet,
} from "lucide-react";
import { AuthContext } from "../../../context/authContext";
import {
  convertDateFormatInUtc,
  convertDateToCT,
  downloadDocument,
  downloadImagesAsZip,
} from "../../../utils/util";
import { getUsers } from "../../../utils/getUser";
import {
  getPayoutInvoice,
  updatePayoutDetails,
  uploadPayoutInvoice,
} from "../../../reducers/payouts.slice";
import { getInvoiceDetails } from "../../../reducers/shipment.slice";
import { generatePDF } from "../../../utils/generateInvoice";
import { getShipmentSummary } from "../../../reducers/warehouse.slice";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import StatusBadge from "../../../components/shared/StatusBadge";
import DataTable from "../../../components/shared/DataTable";
import useMediaQuery from "../../../components/shared/useMediaQuery";

const safeFormatDate = (dateVal, isMember = false) => {
  if (!dateVal || dateVal === "NA" || dateVal === "Invalid Date" || dateVal === "—") return "—";
  try {
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return "—";
    return isMember
      ? convertDateFormatInUtc(dateVal, "MMMM D, YYYY h:mm A")
      : convertDateToCT(dateVal, "MMMM D, YYYY h:mm A");
  } catch {
    return "—";
  }
};

const getInitials = (name) => {
  if (!name || name === "NA" || name === "—") return "—";
  const parts = name.trim().split(" ").filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const ViewPayoutPage = ({ dialogMode = false, payoutDetail: payoutDetailProp, onUpdated }) => {
  const [payoutDetail, setPayoutDetail] = useState({});
  const [showLoader, setShowLoader] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});
  const [openRejectPayout, setOpenRejectPayout] = useState(false);
  const [notes, setNotes] = useState("");
  const [payoutType, setPayoutType] = useState("ACH");
  const [paymentDetails, setPaymentDetails] = useState({});
  const [invoiceDetails, setInvoiceDetails] = useState({});
  const [approvePayout, setApprovePayout] = useState(false);
  const [showOverCreditConfirm, setShowOverCreditConfirm] = useState(false);
  const [amount, setAmount] = useState("");
  const [shipmentSummaryDetail, setShipmentSummaryDetail] = useState([]);

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = getUsers();

  const { showToast } = useContext(AuthContext);
  const isMobile = useMediaQuery("(max-width: 768px)");

  useEffect(() => {
    const detail = payoutDetailProp || location.state?.payoutDetail;
    if (!detail) return;
    setPayoutDetail(detail);
    setPaymentDetails(detail?.payment_details);
    setPayoutType(detail?.payment_details?.payment_method);
    setAmount(detail?.amount);
  }, [payoutDetailProp]);

  useEffect(() => {
    const detail = payoutDetailProp || location.state?.payoutDetail;
    if (!detail) return;

    setShowLoader(true);
    dispatch(getInvoiceDetails({ id: detail?.shipment_details?.id })).then((response) => {
      if (response?.error && Object.keys(response?.error)?.length > 0) {
        setShowLoader(false);
        showToast(response?.error?.message, "error");
        return;
      }
      setInvoiceDetails(response?.payload);
      dispatch(getShipmentSummary(detail?.shipmentId)).then((shipmentResponse) => {
        if (shipmentResponse?.error && Object.keys(shipmentResponse?.error)?.length > 0) {
          setShowLoader(false);
          showToast(shipmentResponse?.error?.message, "error");
          return;
        }
        setShipmentSummaryDetail(shipmentResponse?.payload);
        setShowLoader(false);
      });
    });
  }, [payoutDetailProp]);

  const handleDenyPayment = () => {
    setShowLoader(true);
    const payload = {
      id: payoutDetail?.id,
      status: "CREDIT_DENIED",
    };
    if (notes) payload.notes = notes;
    dispatch(updatePayoutDetails(payload)).then((res) => {
      setShowLoader(false);
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        showToast(res?.error?.message, "error");
        return;
      }
      setOpenRejectPayout(false);
      showToast("Payout denied successfully", "success");
      setNotes("");
      if (dialogMode && onUpdated) onUpdated();
      else navigate("/dashboard/payouts");
    });
  };

  const isOverCredit = parseFloat(amount) > parseFloat(payoutDetail?.amount);

  const handleApproveClick = () => {
    if (isOverCredit) {
      setShowOverCreditConfirm(true);
      return;
    }
    handleAcceptPayment();
  };

  const handleAcceptPayment = () => {
    setShowLoader(true);
    const payload = {
      id: payoutDetail?.id,
      status: "CREDIT_DONE",
      amount: parseFloat(amount),
    };
    if (notes) payload.notes = notes;

    dispatch(updatePayoutDetails(payload)).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      generateInvoice();
    });
  };

  const handleDownloadInvoice = async (type) => {
    setShowLoader(true);
    if (!payoutDetail?.invoice_number) {
      if (!invoiceDetails || Object.keys(invoiceDetails)?.length === 0) {
        showToast("No invoice generated yet", "error");
        setShowLoader(false);
        return;
      }
      const formdata = generatePDF(invoiceDetails);
      const uploadPayload = {
        id: payoutDetail?.id,
        document_type: "INVOICE",
        invoice_number: invoiceDetails?.invoice_number,
      };

      await dispatch(uploadPayoutInvoice({ formdata, uploadPayload })).then((uploadResponse) => {
        if (uploadResponse?.error && Object.keys(uploadResponse?.error)?.length > 0) {
          setShowLoader(false);
          showToast(uploadResponse?.error?.message, "error");
        }
      });
    }
    const payload = {
      payoutId: payoutDetail?.id,
      document_type: type,
    };
    dispatch(getPayoutInvoice(payload)).then((res) => {
      setShowLoader(false);
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        showToast(res?.error?.message, "error");
        return;
      }
      if (!res?.payload || res.payload.length === 0) {
        showToast("No files to download");
        return;
      }
      if (res.payload.length === 1) {
        const item = res.payload[0];
        downloadDocument(
          item?.document_bytes,
          type === "INVOICE" ? `${type} ${payoutDetail?.invoice_number || ""}` : type,
          item?.content_type
        );
      } else {
        downloadImagesAsZip(res.payload, type);
      }
      showToast("File downloaded successfully", "success");
    });
  };

  const generateInvoice = () => {
    setShowLoader(true);
    dispatch(getInvoiceDetails({ id: payoutDetail?.shipmentId })).then((response) => {
      if (response?.error && Object.keys(response?.error)?.length > 0) {
        setShowLoader(false);
        showToast(response?.error?.message, "error");
        return;
      }
      const formdata = generatePDF(response?.payload);
      const uploadPayload = {
        id: payoutDetail?.id,
        document_type: "INVOICE",
        invoice_number: response?.payload?.invoice_number,
      };

      dispatch(uploadPayoutInvoice({ formdata, uploadPayload })).then((uploadResponse) => {
        setShowLoader(false);
        if (uploadResponse?.error && Object.keys(uploadResponse?.error)?.length > 0) {
          showToast(uploadResponse?.error?.message, "error");
          return;
        }
        setApprovePayout(false);
        showToast("Payout approved successfully", "success");
        setNotes("");
        if (dialogMode && onUpdated) onUpdated();
        else navigate("/dashboard/payouts");
      });
    });
  };

  const shipmentSummaryColumns = [
    { key: "upc", label: "UPC", width: 140 },
    { key: "item_name", label: "Item Name" },
    { key: "total_quantity", label: "Received Qty", align: "center", width: 100 },
    { key: "good_quantity", label: "Good Qty", align: "center", width: 90 },
    { key: "damaged_quantity", label: "Damaged Qty", align: "center", width: 100 },
    { key: "shipped_quantity", label: "Shipped Qty", align: "center", width: 100 },
  ];

  const table_columns = [
    { key: "order_id", label: "Order ID", width: 130 },
    { key: "name", label: "Item Name" },
    { key: "quantity", label: "QTY", align: "center", width: 70 },
    {
      key: "cashout_price",
      label: "Unit Price",
      align: "right",
      width: 100,
      render: (row) => `$${row?.cashout_price ?? 0}`,
    },
    {
      key: "cashout_price_total",
      label: "Total",
      align: "right",
      width: 110,
      render: (row) => (
        <span style={{ fontWeight: 700, color: "#fff" }}>
          ${(row?.cashout_price || 0) * (row?.quantity || 0)}
        </span>
      ),
    },
  ];

  const allowedStatuses = [
    "EARLY_PAYMENT",
    "PAYMENT_READY",
    "PAYMENT_PENDING",
    "EARLY_CREDIT",
    "CREDIT_READY",
    "CREDIT_PENDING",
  ];

  const getStatusLabel = (status) => {
    if (status === "CREDIT_DONE" || status === "PAYMENT_DONE") return "Credited";
    return undefined;
  };

  // Safe data resolution
  const memberName = payoutDetail?.member_full_name || "—";

  const initials = getInitials(memberName);
  const shipmentId = payoutDetail?.shipmentId || payoutDetail?.shipment_details?.id || "—";
  const shipmentCarrier = payoutDetail?.shipmentType || payoutDetail?.shipment_details?.shipment_type || "—";
  const shipmentStatus = payoutDetail?.shipmentStatus || payoutDetail?.shipment_details?.status || "—";

  const rawShipmentDate = payoutDetail?.shipmentDate || payoutDetail?.shipment_details?.shipment_date;
  const formattedShipmentDate = safeFormatDate(rawShipmentDate, user?.role === "MEMBER");

  const rawPaymentDate = payoutDetail?.paymentDate || payoutDetail?.payment_date;
  const formattedPaymentDate = safeFormatDate(rawPaymentDate, user?.role === "MEMBER");

  const formattedAmount = payoutDetail?.totalAmount
    ? payoutDetail.totalAmount
    : payoutDetail?.amount !== undefined
    ? `$${Number(payoutDetail.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : "$0.00";

  return (
    <>
      {showLoader && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 70,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.5)",
            backdropFilter: "blur(4px)",
            color: "#fff",
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          <div
            style={{
              padding: "12px 24px",
              borderRadius: 10,
              background: "#161616",
              border: "1px solid rgba(255, 255, 255, 0.08)",
              boxShadow: "0 8px 30px rgba(0,0,0,0.6)",
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <span>Loading...</span>
          </div>
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: dialogMode ? 0 : "24px 16px" }}>
        <div
          style={{
            width: "100%",
            maxWidth: 860,
            padding: dialogMode ? 0 : "24px",
            backgroundColor: dialogMode ? "transparent" : "#101010",
            borderRadius: dialogMode ? 0 : 14,
            border: dialogMode ? "none" : "1px solid rgba(255,255,255,0.08)",
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        >
          {!dialogMode && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
              <button
                type="button"
                onClick={() => navigate(-1)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  color: "#8b9296",
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  padding: "6px 12px",
                  borderRadius: 8,
                  cursor: "pointer",
                  fontSize: 12.5,
                  fontWeight: 600,
                  transition: "all 0.15s ease",
                }}
              >
                <ArrowLeft size={14} /> Back to Payouts
              </button>
              <h1 style={{ fontSize: "1.35rem", color: "#fff", fontWeight: 800, margin: 0 }}>
                Payout Summary
              </h1>
            </div>
          )}

          {/* HERO FINANCIAL METRIC CARD */}
          <div
            style={{
              background: "rgba(255, 255, 255, 0.02)",
              border: "1px solid rgba(255, 255, 255, 0.07)",
              borderRadius: 12,
              padding: isMobile ? "16px 18px" : "20px 24px",
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "space-between",
              alignItems: isMobile ? "flex-start" : "center",
              gap: 16,
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span
                  style={{
                    fontFamily: "ui-monospace, SF Mono, Menlo, monospace",
                    fontSize: 11.5,
                    fontWeight: 600,
                    color: "#94a3b8",
                    background: "rgba(255, 255, 255, 0.05)",
                    border: "1px solid rgba(255, 255, 255, 0.08)",
                    padding: "2px 8px",
                    borderRadius: 6,
                    letterSpacing: "0.02em",
                  }}
                >
                  {payoutDetail?.id || "PAYOUT"}
                </span>
                <span style={{ fontSize: 12, color: "#475569" }}>•</span>
                <span style={{ fontSize: 13, color: "#cbd5e1", fontWeight: 600 }}>
                  {memberName}
                </span>
              </div>

              <div style={{ display: "flex", flexDirection: "column", marginTop: 4 }}>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: "0.05em",
                    color: "#64748b",
                    textTransform: "uppercase",
                  }}
                >
                  Total Payout Amount
                </span>
                <span
                  style={{
                    fontSize: isMobile ? 26 : 32,
                    fontWeight: 700,
                    color: "#ffffff",
                    fontFamily: "ui-monospace, SF Mono, Menlo, monospace",
                    letterSpacing: "-0.02em",
                    lineHeight: 1.15,
                    marginTop: 2,
                  }}
                >
                  {formattedAmount}
                </span>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: isMobile ? "row" : "column",
                alignItems: isMobile ? "center" : "flex-end",
                justifyContent: isMobile ? "space-between" : "center",
                width: isMobile ? "100%" : "auto",
                gap: 8,
              }}
            >
              <StatusBadge
                status={payoutDetail?.status}
                label={getStatusLabel(payoutDetail?.status)}
              />
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  padding: "4px 9px",
                  borderRadius: 6,
                  background: "rgba(255, 255, 255, 0.04)",
                  border: "1px solid rgba(255, 255, 255, 0.07)",
                  color: "#94a3b8",
                  fontSize: 11.5,
                  fontWeight: 600,
                }}
              >
                <Wallet size={12} color="#94a3b8" />
                <span>{payoutType || "—"}</span>
              </div>
            </div>
          </div>

          {/* BENTO INFO GRID (2 COLUMNS) */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: 14,
            }}
          >
            {/* Card 1: Recipient Details */}
            <div
              style={{
                background: "rgba(255, 255, 255, 0.02)",
                border: "1px solid rgba(255, 255, 255, 0.06)",
                borderRadius: 12,
                padding: "16px",
                display: "flex",
                flexDirection: "column",
                gap: 12,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 7, paddingBottom: 8, borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                <User size={14} color="#94a3b8" />
                <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: "0.05em", color: "#f1f5f9", textTransform: "uppercase" }}>
                  Recipient & Account
                </span>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {/* Member Name with Initials Avatar */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 12.5, color: "#64748b", fontWeight: 500 }}>Member Name</span>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        background: "rgba(255, 255, 255, 0.08)",
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                        color: "#e2e8f0",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 10,
                        fontWeight: 700,
                      }}
                    >
                      {initials}
                    </span>
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: "#f8fafc" }}>
                      {memberName}
                    </span>
                  </div>
                </div>

                {/* Settlement Method */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 12.5, color: "#64748b", fontWeight: 500 }}>Payment Method</span>
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: "#f8fafc" }}>
                    {payoutType || "—"}
                  </span>
                </div>

                {/* Payment Date */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 12.5, color: "#64748b", fontWeight: 500 }}>Payment Date</span>
                  <span style={{ fontSize: 12.5, fontWeight: 500, color: formattedPaymentDate !== "—" ? "#e2e8f0" : "#475569" }}>
                    {formattedPaymentDate}
                  </span>
                </div>

                {/* Notes */}
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
                  <span style={{ fontSize: 12.5, color: "#64748b", fontWeight: 500 }}>Notes</span>
                  <span
                    style={{
                      fontSize: 12,
                      color: payoutDetail?.notes && payoutDetail.notes !== "NA" ? "#cbd5e1" : "#475569",
                      textAlign: "right",
                      whiteSpace: "pre-line",
                      maxWidth: "60%",
                    }}
                  >
                    {payoutDetail?.notes && payoutDetail.notes !== "NA" ? payoutDetail.notes : "—"}
                  </span>
                </div>
              </div>
            </div>

            {/* Card 2: Shipment & Logistics */}
            <div
              style={{
                background: "rgba(255, 255, 255, 0.02)",
                border: "1px solid rgba(255, 255, 255, 0.06)",
                borderRadius: 12,
                padding: "16px",
                display: "flex",
                flexDirection: "column",
                gap: 12,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 7, paddingBottom: 8, borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                <Truck size={14} color="#94a3b8" />
                <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: "0.05em", color: "#f1f5f9", textTransform: "uppercase" }}>
                  Shipment & Tracking
                </span>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {/* Shipment ID */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 12.5, color: "#64748b", fontWeight: 500 }}>Shipment ID</span>
                  <span
                    style={{
                      fontFamily: "ui-monospace, SF Mono, Menlo, monospace",
                      fontSize: 11.5,
                      fontWeight: 600,
                      color: "#e2e8f0",
                      background: "rgba(255, 255, 255, 0.04)",
                      border: "1px solid rgba(255, 255, 255, 0.08)",
                      padding: "2px 7px",
                      borderRadius: 5,
                    }}
                  >
                    {shipmentId}
                  </span>
                </div>

                {/* Carrier */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 12.5, color: "#64748b", fontWeight: 500 }}>Shipment Type</span>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                      fontSize: 11.5,
                      fontWeight: 600,
                      color: "#e2e8f0",
                      background: "rgba(255, 255, 255, 0.04)",
                      border: "1px solid rgba(255, 255, 255, 0.08)",
                      padding: "2px 8px",
                      borderRadius: 6,
                    }}
                  >
                    <Truck size={12} color="#94a3b8" />
                    {shipmentCarrier}
                  </span>
                </div>

                {/* Shipment Status */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 12.5, color: "#64748b", fontWeight: 500 }}>Shipment Status</span>
                  <StatusBadge status={shipmentStatus} />
                </div>

                {/* Shipment Date */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 12.5, color: "#64748b", fontWeight: 500 }}>Shipment Date</span>
                  <span style={{ fontSize: 12.5, fontWeight: 500, color: formattedShipmentDate !== "—" ? "#e2e8f0" : "#475569" }}>
                    {formattedShipmentDate}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* SHIPMENT SUMMARY (RECEIVED ITEMS) */}
          {shipmentSummaryDetail?.length > 0 && (
            <div
              style={{
                background: "rgba(255, 255, 255, 0.02)",
                border: "1px solid rgba(255, 255, 255, 0.06)",
                borderRadius: 12,
                padding: "16px",
                display: "flex",
                flexDirection: "column",
                gap: 12,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <PackageCheck size={14} color="#94a3b8" />
                  <span style={{ fontSize: 11.5, fontWeight: 700, color: "#f1f5f9", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    Shipment Items Summary
                  </span>
                </div>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#94a3b8",
                    background: "rgba(255,255,255,0.05)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    padding: "2px 8px",
                    borderRadius: 999,
                  }}
                >
                  {shipmentSummaryDetail.length} {shipmentSummaryDetail.length === 1 ? "item" : "items"}
                </span>
              </div>

              <div style={{ borderRadius: 8, overflow: "hidden", border: "1px solid rgba(255,255,255,0.06)" }}>
                <DataTable
                  columns={shipmentSummaryColumns}
                  rows={shipmentSummaryDetail}
                  getRowStyle={(row) =>
                    row?.discrepancy_found === true || Number(row?.damaged_quantity) > 0
                      ? { backgroundColor: "rgba(239,68,68,0.1)" }
                      : undefined
                  }
                />
              </div>
            </div>
          )}

          {/* INVOICE SUMMARY */}
          {invoiceDetails?.shipment_boxes?.length > 0 && (
            <div
              style={{
                background: "rgba(255, 255, 255, 0.02)",
                border: "1px solid rgba(255, 255, 255, 0.06)",
                borderRadius: 12,
                padding: "16px",
                display: "flex",
                flexDirection: "column",
                gap: 12,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Receipt size={14} color="#94a3b8" />
                  <span style={{ fontSize: 11.5, fontWeight: 700, color: "#f1f5f9", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    Invoice Summary
                  </span>
                  {payoutDetail?.invoice_number && (
                    <span
                      style={{
                        fontFamily: "ui-monospace, SF Mono, Menlo, monospace",
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#94a3b8",
                        background: "rgba(255, 255, 255, 0.05)",
                        border: "1px solid rgba(255, 255, 255, 0.08)",
                        padding: "2px 6px",
                        borderRadius: 4,
                      }}
                    >
                      {payoutDetail.invoice_number}
                    </span>
                  )}
                </div>

                <Button
                  variant="outline"
                  size="xs"
                  onClick={() => handleDownloadInvoice("INVOICE")}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    borderColor: "rgba(255, 255, 255, 0.12)",
                    color: "#e2e8f0",
                    background: "rgba(255, 255, 255, 0.04)",
                    fontSize: 11.5,
                    fontWeight: 600,
                    height: 28,
                    padding: "0 10px",
                    borderRadius: 6,
                  }}
                >
                  <Download size={13} color="#94a3b8" />
                  Download Invoice
                </Button>
              </div>

              <div style={{ borderRadius: 8, overflow: "hidden", border: "1px solid rgba(255,255,255,0.06)" }}>
                <DataTable
                  columns={table_columns}
                  rows={invoiceDetails?.shipment_boxes}
                />
              </div>
            </div>
          )}

          {/* ACTION BUTTONS (FOR AUTHORIZED ADMINS) */}
          {allowedStatuses.includes(payoutDetail.status) && user?.role !== "MEMBER" && (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 10,
                paddingTop: 12,
                borderTop: "1px solid rgba(255, 255, 255, 0.06)",
              }}
            >
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setOpenRejectPayout(true);
                  setNotes("");
                }}
                style={{
                  color: "#ef4444",
                  borderColor: "rgba(239, 68, 68, 0.25)",
                  background: "rgba(239, 68, 68, 0.05)",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontWeight: 600,
                  borderRadius: 8,
                }}
              >
                <X size={14} strokeWidth={2.5} />
                <span>Deny Request</span>
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  setApprovePayout(true);
                  setNotes("");
                }}
                style={{
                  background: "#00ADB5",
                  color: "#000",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontWeight: 700,
                  border: "none",
                  borderRadius: 8,
                }}
              >
                <Check size={14} strokeWidth={2.5} />
                <span>Approve Payout</span>
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* APPROVE DIALOG */}
      <Dialog open={approvePayout} onOpenChange={(open) => {
        if (!open) {
          setApprovePayout(false);
          setAmount(payoutDetail?.amount);
        }
      }}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Approve Wallet Credit Request?</DialogTitle>
            <DialogDescription>
              You are about to approve this request. Once approved, the amount will be credited to
              the member's wallet and cannot be reversed.
            </DialogDescription>
          </DialogHeader>

          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <Label>Enter Amount</Label>
              <Input
                value={amount}
                placeholder="Please Enter Your Amount"
                onChange={(e) => setAmount(e.target.value)}
                onKeyDown={(e) => {
                  const invalidChars = ["e", "E", ",", "+", "-"];
                  if (e.key === "." && e.target.value.includes(".")) {
                    e.preventDefault();
                    return;
                  }
                  if (
                    invalidChars.includes(e.key) ||
                    (!/^\d$/.test(e.key) &&
                      e.key !== "." &&
                      !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Enter", "Home", "End"].includes(e.key) &&
                      !e.ctrlKey &&
                      !e.metaKey)
                  ) {
                    e.preventDefault();
                  }
                }}
                onPaste={(e) => {
                  const pasteData = e.clipboardData.getData("text");
                  if (!/^\d*\.?\d*$/.test(pasteData)) e.preventDefault();
                }}
                onWheel={(e) => e.target.blur()}
              />
              {parseFloat(amount) === 0 && (
                <span style={{ color: "#f27059", fontSize: 12 }}>Amount cannot be zero</span>
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <Label>Reason for edit</Label>
              <Textarea
                value={notes}
                placeholder="Provide a reason here"
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setApprovePayout(false);
                setAmount(payoutDetail?.amount);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleApproveClick}
              disabled={!amount || parseFloat(amount) === 0}
            >
              Approve Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* OVER-CREDIT CONFIRM DIALOG */}
      <Dialog open={showOverCreditConfirm} onOpenChange={setShowOverCreditConfirm}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Confirm Over-Credit</DialogTitle>
            <DialogDescription>
              You are crediting ${parseFloat(amount).toFixed(2)} which is $
              {(parseFloat(amount) - parseFloat(payoutDetail?.amount)).toFixed(2)} more than the
              original payout amount of ${parseFloat(payoutDetail?.amount).toFixed(2)}. Are you sure?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOverCreditConfirm(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                setShowOverCreditConfirm(false);
                handleAcceptPayment();
              }}
            >
              Confirm Over-Credit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* REJECT DIALOG */}
      <Dialog open={openRejectPayout} onOpenChange={setOpenRejectPayout}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Are you sure you want to reject this request?</DialogTitle>
            <DialogDescription>
              This action will notify the member of your decision. You can provide a reason in the
              notes section below to help them understand the denial.
            </DialogDescription>
          </DialogHeader>

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <Label>Reason</Label>
            <Textarea
              value={notes}
              placeholder="Provide a reason here"
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenRejectPayout(false)}>
              Cancel
            </Button>
            <Button variant="outline" onClick={handleDenyPayment} style={{ color: "#f27059" }}>
              Deny
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ViewPayoutPage;
